import { Log, LogCategory, Operation as LogOperation } from '@augloop/logging';
import { AddOperation, DeltaUpdateOperation, UpdateOperation } from '@augloop/types-core';
import { ExceptionType } from '@augloop/schemas';
var SubmitReason;
(function (SubmitReason) {
    SubmitReason[SubmitReason["ExceedingMaxSize"] = 0] = "ExceedingMaxSize";
    SubmitReason[SubmitReason["GroupComplete"] = 1] = "GroupComplete";
    SubmitReason[SubmitReason["BatchIntervalElapsed"] = 2] = "BatchIntervalElapsed";
})(SubmitReason || (SubmitReason = {}));
export class BatchManager {
    constructor(submit, reduceBatchOperationsEnabled, batchMessagesEnabled, onSummary, maxNumberOfDeltaUpdateOpsPerItemPerBatchEnabled) {
        this.batchesByGroupingKey = new Map();
        this.getOrderOfMagnitudeDimension = (size) => {
            if (size < 0) {
                return 'Negative';
            }
            if (size === 0) {
                return '0';
            }
            if (size === 1) {
                return '1';
            }
            return `Magnitude ${size.toString().length}`;
        };
        this.submit = submit;
        this.onSummary = onSummary;
        this.reduceBatchOperationsEnabled = reduceBatchOperationsEnabled;
        this.batchMessagesEnabled = batchMessagesEnabled;
        this.maxNumberOfDeltaUpdateOpsPerItemPerBatchEnabled = maxNumberOfDeltaUpdateOpsPerItemPerBatchEnabled;
    }
    addBatchItem(input, batchConfig, context, cv, groupComplete, callback) {
        const { delayMs, delayMsMax, maxInputSize, estimateSize, groupingKeyExtractor } = batchConfig;
        const inputSize = estimateSize(input);
        const groupingKey = groupingKeyExtractor(input);
        // If input is larger than maxInputSize, split it into multiple inputs
        if (batchConfig.split && inputSize > maxInputSize) {
            let splitResult;
            try {
                splitResult = batchConfig.split(input);
            }
            catch (err) {
                const splitError = new Error('Split error: ' + err.message);
                if (callback) {
                    callback(splitError);
                }
                else {
                    Log.error(0x222ce050 /* tag_8lobq */, LogCategory.CoreDefault, splitError);
                }
                return;
            }
            if (splitResult && Array.isArray(splitResult.inputs) && splitResult.inputs.length > 1) {
                // Make sure we invoke the callback once even though we have multiple inputs
                let error;
                const outputs = [];
                const splitCallback = callback
                    ? (err, output) => {
                        error = error || err; // Take the first error
                        outputs.push(output);
                        if (outputs.length === splitResult.inputs.length) {
                            let output;
                            try {
                                output = splitResult.join(outputs);
                            }
                            catch (err) {
                                callback(new Error('Join error: ' + err.message));
                                return;
                            }
                            callback(error, output);
                        }
                    }
                    : undefined;
                // Add each input to the batch individually and keep groupComplete flag just for the last one
                for (let index = 0; index < splitResult.inputs.length; index++) {
                    const splitInput = splitResult.inputs[index];
                    this.addBatchItem(splitInput, batchConfig, context, cv, groupComplete && index === splitResult.inputs.length - 1, splitCallback);
                }
                return;
            }
        }
        // Get the existing batch and send it if adding the given input to it would increase the size beyond the max
        let batch = this.batchesByGroupingKey.get(groupingKey);
        if (batch && batch.size + inputSize > maxInputSize) {
            this.executeBatch(groupingKey, batch, batchConfig, SubmitReason.ExceedingMaxSize);
            batch = undefined;
        }
        // Form a new batch if necessary
        if (!batch) {
            batch = {
                items: [],
                size: 0,
                hasItemsWithCallbacks: false,
                context: context,
                creationTime: Date.now(),
            };
            this.batchesByGroupingKey.set(groupingKey, batch);
        }
        if (!this.batchMessagesEnabled) {
            // Add the given input to the current batch and schedule it for submission
            batch.items.push({
                input: input,
                size: inputSize,
                callback: callback
            });
        }
        else {
            // Lump the input into the last BatchItem if possible, else add it as a new BatchItem in the batch
            const lastItem = batch.items[batch.items.length - 1];
            if (lastItem && lastItem.cv === cv && lastItem.callback === callback) {
                lastItem.input = [...lastItem.input, ...input];
                lastItem.size += inputSize;
            }
            else {
                batch.items.push({
                    input: input,
                    size: inputSize,
                    cv: cv,
                    callback: callback
                });
            }
        }
        batch.size += inputSize;
        batch.hasItemsWithCallbacks = batch.hasItemsWithCallbacks || !!callback;
        if (!this.batchMessagesEnabled) {
            batch.cv = cv; // Service request will use the last CV seen
        }
        batch.groupComplete = groupComplete;
        if (batch.groupComplete) {
            this.executeBatch(groupingKey, batch, batchConfig, SubmitReason.GroupComplete);
        }
        else {
            // The current batch interval can be extended by resetting the timeout
            // as long as the duration(including the default interval itself) hasn't exceeded the threshold.
            if (Date.now() - batch.creationTime + delayMs < delayMsMax) {
                clearTimeout(batch.timeout);
                batch.timeout = undefined;
            }
            if (!batch.timeout) {
                batch.timeout = setTimeout(() => {
                    this.executeBatch(groupingKey, batch, batchConfig, SubmitReason.BatchIntervalElapsed);
                }, delayMs);
            }
        }
    }
    removeAllBatchedItems() {
        this.batchesByGroupingKey.forEach(batch => {
            if (batch.timeout) {
                clearTimeout(batch.timeout);
            }
        });
        this.batchesByGroupingKey.clear();
    }
    /**
     * Used by executeBatch to reduce the total number of operations in a batch message by
     * deleting redundant UpdateOperations.
     *
     *  Iterates backwards through the array of operations held in multiplexResult, tracking
     * items updated by an UpdateOperation in opsToRemove. Then, if we come across the same item
     * in another UpdateOperation, we can safely remove it from the operation as we know that
     * update is overwritten later by the UpdateOperation we've already seen.
     *  If all the items in a given UpdateOperation have been removed, we discard it, storing all
     * other operations in a temp array.
     *  When we come across an AddOperation, we remove corresponding items from opsToRemove as we
     * know an item cannot be updated prior to being added.
     */
    reduceBatchOperations(multiplexResult) {
        const reduceOp = new LogOperation({
            operationName: 'ReduceBatchOperations'
        }).start();
        let uniqueTiles = 0;
        let updatesRemoved = 0;
        let totalUpdates = 0;
        const opsToRemove = new Set();
        const newBatchItems = [];
        const getOpKey = (operation, item) => {
            return operation.parentPath.toString() + '/' + operation.parentRevId + '/' + item.id;
        };
        for (const batchItem of multiplexResult.input.reverse()) { // Iterate through batchItems backwards
            const newOps = [];
            for (const operation of batchItem.input.reverse()) { // Iterate through operations backwards
                if (UpdateOperation.typeGuard(operation)) {
                    const newItems = [];
                    for (const item of operation.items) {
                        totalUpdates++;
                        const opKey = getOpKey(operation, item);
                        if (!opsToRemove.has(opKey)) { // Only update item if not later overwritten
                            newItems.push(item);
                            opsToRemove.add(opKey);
                            uniqueTiles++;
                        }
                        else {
                            updatesRemoved++;
                        }
                    }
                    if (newItems.length !== 0) { // Only keep operation if items list not empty
                        operation.items = newItems;
                        newOps.push(operation);
                    }
                }
                else if (AddOperation.typeGuard(operation)) { // Don't keep checking against items yet to be added
                    for (const item of operation.items) {
                        const opKey = getOpKey(operation, item);
                        opsToRemove.delete(opKey);
                    }
                    newOps.push(operation);
                }
                else {
                    newOps.push(operation);
                }
            }
            // Store the batchItem only if it contains ops
            if (newOps.length !== 0) {
                newBatchItems.push({
                    input: newOps.reverse(),
                    size: batchItem.size,
                    cv: batchItem.cv,
                    callback: batchItem.callback
                });
            }
        }
        multiplexResult.input = newBatchItems.reverse(); // Fix order of batchItems
        if (totalUpdates != 0) {
            reduceOp.dimension0 = `${this.getOrderOfMagnitudeDimension(updatesRemoved)}`;
            reduceOp.dimension1 = `noOp: ${updatesRemoved === 0}`;
        }
        if (totalUpdates - updatesRemoved == uniqueTiles) { // Should be left with one update per tile
            reduceOp.success = true;
        }
        else {
            reduceOp.success = false;
        }
        Log.info(0x1e450400 /* tag_4rqqa */, LogCategory.CoreDefault, reduceOp.stop());
    }
    /**
     * Temporary telemetry to root cause high volume of DeltaUpdateOps per second per item.
     * This is causing a high execution volume of OARouterWorkflow.
     */
    maxNumberOfDeltaUpdateOpsPerItemPerBatch(multiplexResult) {
        const getItemKey = (operation, item) => {
            return operation.parentPath.toString() + '/' + item.id;
        };
        let maxNumberOfDeltaItemsPerId = 0;
        const numberOfDeltaItemsPerId = new Map();
        for (const operation of multiplexResult.input) {
            if (DeltaUpdateOperation.typeGuard(operation)) {
                for (const updateItem of operation.items) {
                    const currId = getItemKey(operation, updateItem);
                    if (!numberOfDeltaItemsPerId.has(currId)) {
                        numberOfDeltaItemsPerId.set(currId, 0);
                    }
                    numberOfDeltaItemsPerId.set(currId, numberOfDeltaItemsPerId.get(currId) + 1);
                    maxNumberOfDeltaItemsPerId = Math.max(maxNumberOfDeltaItemsPerId, numberOfDeltaItemsPerId.get(currId));
                }
            }
        }
        return maxNumberOfDeltaItemsPerId;
    }
    executeBatch(groupingKey, batch, batchConfig, submitReason) {
        clearTimeout(batch.timeout); // No-op if undefined/invalid
        batch.timeout = undefined;
        this.batchesByGroupingKey.delete(groupingKey);
        const batchItems = batch.items;
        const op = new LogOperation({
            operationName: 'ExecuteBatch',
            cv: !this.batchMessagesEnabled ? batch.cv : '',
            resourceId: batch.context.name,
            resultDescription: `batching duration: ${Date.now() - batch.creationTime}ms`,
            dimension0: `${!this.batchMessagesEnabled ? 'Batched ops count:' : 'Batched items count'} ${this.getOrderOfMagnitudeDimension(batchItems.length)}`,
            dimension1: `Size ${this.getOrderOfMagnitudeDimension(batch.size)}`,
            dimension2: submitReason.toString()
        }).start();
        const invokeOutputCallbacksWithError = (operation, err, output) => {
            let error;
            if (err && err.exceptionType === ExceptionType.NoOutput) {
                op.success = true;
                Log.info(0x222ce051 /* tag_8lobr */, LogCategory.CoreDefault, op.stop());
            }
            else {
                const errorMessage = `${operation} error: ${err ? (err.message || err) : 'Unknown error'}`;
                error = err || new Error(errorMessage);
                op.success = false;
                op.resultDescription = `Batch of ${batchItems.length} items of size ${batch.size} with ${errorMessage}`;
                op.resultSignature = `${operation} Error`;
                Log.error(0x222ce052 /* tag_8lobs */, LogCategory.CoreDefault, op.stop());
            }
            if (batch.hasItemsWithCallbacks) {
                for (const batchItem of batchItems) {
                    if (batchItem.callback) {
                        batchItem.callback(error, output);
                    }
                }
            }
        };
        // Combine batch into multiplexResult
        let multiplexResult;
        try {
            multiplexResult = batchConfig.multiplex(!this.batchMessagesEnabled ? batchItems.map(x => x.input) : batchItems);
        }
        catch (err) {
            invokeOutputCallbacksWithError('Multiplex', err);
            return;
        }
        // Remove overwritten update operations
        if (this.reduceBatchOperationsEnabled && this.batchMessagesEnabled) {
            if (groupingKey == 'operations') {
                this.reduceBatchOperations(multiplexResult);
            }
        }
        if (!this.batchMessagesEnabled && groupingKey == 'operations' && this.maxNumberOfDeltaUpdateOpsPerItemPerBatchEnabled) {
            op.dimension3 = this.getOrderOfMagnitudeDimension(this.maxNumberOfDeltaUpdateOpsPerItemPerBatch(multiplexResult));
        }
        // submit batch
        this.submit(multiplexResult.input, batch, (err, output) => {
            if (err || (output && ('exceptionType' in output || output instanceof Error))) {
                invokeOutputCallbacksWithError('Submit', err || output, output);
            }
            else if (batch.hasItemsWithCallbacks || batchConfig.summaryExtractor) {
                let outputs;
                if (batch.hasItemsWithCallbacks) {
                    try {
                        outputs = multiplexResult.demultiplex(output);
                        if (outputs.length !== batchItems.length) {
                            throw new Error('Mismatched output length');
                        }
                    }
                    catch (err) {
                        invokeOutputCallbacksWithError('Demultiplex', err);
                        return;
                    }
                }
                let summary;
                let summarySchemaName;
                if (batchConfig.summaryExtractor) {
                    try {
                        [summarySchemaName, summary] = batchConfig.summaryExtractor(output);
                    }
                    catch (err) {
                        invokeOutputCallbacksWithError('SummaryExtractor', err);
                        return;
                    }
                }
                op.success = true;
                Log.info(0x222ce046 /* tag_8lobg */, LogCategory.CoreDefault, op.stop());
                if (batch.hasItemsWithCallbacks) {
                    for (let i = 0; i < batchItems.length; i++) {
                        if (batchItems[i].callback) {
                            batchItems[i].callback(undefined, outputs[i]);
                        }
                    }
                }
                if (summary) {
                    this.onSummary(summarySchemaName, summary, batch.context);
                }
            }
            else {
                op.success = true;
                Log.info(0x222ce047 /* tag_8lobh */, LogCategory.CoreDefault, op.stop());
            }
        });
    }
}
//# sourceMappingURL=batch-manager.js.map